import { VectorSourceSpecification } from 'maplibre-gl';
import { useCallback, useMemo } from 'react';

import { ITASCA_URL, PENROSE_URL } from '@/constants/urls';
import { sourceConfig } from '@/hooks/map-box/config/sourceConfig';
import { useTileConfig } from '@/hooks/useTileConfig';
import type { TileConfig } from '@/models/tileConfig';

export type CoreVectorSourceState = {
  stateSourceData: VectorSourceSpecification | null;
  countySourceData: VectorSourceSpecification | null;
  townshipSourceData: VectorSourceSpecification | null;
  sectionSourceData: VectorSourceSpecification | null;
  parcelCentroidsSourceData: VectorSourceSpecification | null;
  parcelsSourceData: VectorSourceSpecification | null;
  cluSourceData: VectorSourceSpecification | null;
};

export const emptyCoreVectorSourceState: CoreVectorSourceState = {
  cluSourceData: null,
  countySourceData: null,
  parcelCentroidsSourceData: null,
  parcelsSourceData: null,
  sectionSourceData: null,
  stateSourceData: null,
  townshipSourceData: null,
};

export const useCoreVectorSourceState = (): CoreVectorSourceState => {
  const { tileConfig } = useTileConfig();

  const buildSourceDataFromTileConfig = useCallback(
    (key: keyof TileConfig): VectorSourceSpecification | null => {
      if (!tileConfig || !sourceConfig) return null;

      const url = tileConfig[key];
      if (typeof url !== 'string') return null;

      const config = sourceConfig[key];
      if (!config || !url) return null;

      const shouldAppendPBF =
        !url.includes(ITASCA_URL) && !url.includes(PENROSE_URL);

      return {
        maxzoom: config.maxZoom,
        minzoom: config.minZoom,
        promoteId: config.promoteId,
        tiles: [`${url}{z}/{x}/{y}${shouldAppendPBF ? '.pbf' : ''}`],
        type: 'vector',
      };
    },
    [tileConfig]
  );

  const cluSourceData = useMemo(
    () => buildSourceDataFromTileConfig('clu'),
    [buildSourceDataFromTileConfig]
  );

  const countySourceData = useMemo(
    () => buildSourceDataFromTileConfig('counties'),
    [buildSourceDataFromTileConfig]
  );

  const parcelCentroidsSourceData = useMemo(
    () => buildSourceDataFromTileConfig('parcel-centroids'),
    [buildSourceDataFromTileConfig]
  );

  const parcelsSourceData = useMemo(
    () => buildSourceDataFromTileConfig('parcels'),
    [buildSourceDataFromTileConfig]
  );

  const sectionSourceData = useMemo(
    () => buildSourceDataFromTileConfig('sections'),
    [buildSourceDataFromTileConfig]
  );

  const stateSourceData = useMemo(
    () => buildSourceDataFromTileConfig('states'),
    [buildSourceDataFromTileConfig]
  );

  const townshipSourceData = useMemo(
    () => buildSourceDataFromTileConfig('county-subdivisions'),
    [buildSourceDataFromTileConfig]
  );

  return {
    cluSourceData,
    countySourceData,
    parcelCentroidsSourceData,
    parcelsSourceData,
    sectionSourceData,
    stateSourceData,
    townshipSourceData,
  };
};
